import React from "react";
import { Box } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { COLORS } from "../../../assets/theme";
import CustomTable from "../../../components/table/CustomTable";
import SearchHeaderContainer from "../../../components/table/SearchHeaderContainer";
import CustomLabel from "../../../components/CustomLabel";
import PageHeader from "../../../components/PageHeader";
import { useParams } from "react-router-dom";
import { getProfitAndLoss } from "../../../services/newReportsService";
import { getAllBranches } from "../../../services/BranchesService";

const ProfitLossReport: React.FC = () => {
  const params = useParams<{ businessId?: string; id?: string }>();
  const businessId = params.businessId || params.id;

  if (!businessId) {
    throw new Error("Profit & Loss report requires businessId in route");
  }

  const [rows, setRows] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [units, setUnits] = React.useState<any[]>([]);
  const [selectValue, setSelectValue] = React.useState<string | number>("all");

  const [dateRange, setDateRange] = React.useState<
    { start?: string; end?: string } | null
  >(() => {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));
    return { start: start.toISOString(), end: end.toISOString() };
  });

  const buildDateBoundary = (raw: string | Date | null | undefined, endOfDay: boolean) => {
    if (!raw) return null;
    if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split("-").map(Number);
      return new Date(Date.UTC(y, m - 1, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0));
    }
    const parsed = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(
      Date.UTC(
        parsed.getUTCFullYear(),
        parsed.getUTCMonth(),
        parsed.getUTCDate(),
        endOfDay ? 23 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 999 : 0,
      ),
    );
  };

  const normalizeRangeIso = (range: { start?: string; end?: string } | null) => {
    if (!range) return { from: undefined as string | undefined, to: undefined as string | undefined };
    const fromDate = buildDateBoundary(range.start, false);
    const toDate = buildDateBoundary(range.end, true);
    return {
      from: fromDate ? fromDate.toISOString() : undefined,
      to: toDate ? toDate.toISOString() : undefined,
    };
  };

  const getUnitId = (u: any) => u.id ?? u.unit_id;

  // Fetch Units
  React.useEffect(() => {
    const fetchUnits = async () => {
      try {
        const res = await getAllBranches(businessId);
        const unitsData = res?.units || res?.data || res || [];
        setUnits(Array.isArray(unitsData) ? unitsData : []);
      } catch {
        setUnits([]);
      }
    };

    fetchUnits();
  }, [businessId]);

  // Fetch P&L
  React.useEffect(() => {
    const fetchPnL = async () => {
      setLoading(true);

      try {
        type ProfitAndLossResponse = {
  revenue: number;
  expenses: number;
  net: number;
};
        const params: any = {
          businessId,
          ...normalizeRangeIso(dateRange),
        };

        if (selectValue !== "all") {
          params.unitId = String(selectValue);
        }

        const res = await getProfitAndLoss(params) as ProfitAndLossResponse;

        // Normalize into table rows
        const data = [
          { label: "Revenue", value: res?.revenue ?? 0 },
          { label: "Expenses", value: res?.expenses ?? 0 },
          { label: "Net Profit", value: res?.net ?? 0 },
        ];

        setRows(data);
      } catch (err) {
        console.error("Failed to fetch P&L:", err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPnL();
  }, [businessId, selectValue, dateRange]);

  const exportCsv = React.useCallback(() => {
    if (!rows.length) return;

    const keys = ["label", "value"];
    const header = keys.join(",");
    const lines = rows.map((r) => {
      const row: Record<string, any> = {
        label: r.label ?? "-",
        value: r.value ?? "-",
      };
      return keys.map((k) => `"${String(row[k] ?? "-").replace(/"/g, '""')}"`).join(",");
    });

    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pnl-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [rows]);

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <PageHeader
        title="Profit & Loss"
        subtitle="Income against expenses for the selected period."
        sx={{ mt: { xs: 2, sm: 3 } }}
      />

      <SearchHeaderContainer
        search={search}
        setSearch={setSearch}
        dateRangeConfig={{
          value: dateRange ?? undefined,
          onChange: (v: any) => setDateRange(v),
        }}
        selectConfig={{
          options: [
            { label: "All", value: "all" },
            ...units.map((u) => ({
              label: u.name || "Unknown Branch",
              value: getUnitId(u),
            })),
          ],
          value: selectValue,
          onChange: (v: any) => setSelectValue(v),
          label: "Select Branch",
        }}
        buttonConfig={[
          {
            label: "Export CSV",
            onClick: () => Promise.resolve(exportCsv()),
            startIcon: <FileDownloadIcon />,
            bgColor: COLORS.MainColor,
            textColor: "#fff",
          },
        ]}
      />

      <CustomTable<any>
        filters={{ page: 0, size: 10, search }}
        clientSidePagination
        loading={loading}
        fields={[
          { key: "label", header: "Type" },
          { key: "value", header: "Amount" },
        ]}
        fetchFunc={async (params?: any) => {
          const q = String(params?.search || search || "")
            .toLowerCase()
            .trim();

          const filtered = rows.filter((r) => {
            if (q) {
              const text = `${r.label} ${r.value}`.toLowerCase();
              if (!text.includes(q)) return false;
            }
            return true;
          });

          return {
            content: filtered,
            number: 0,
            size: params?.size || 10,
            totalElements: filtered.length,
            totalPages: Math.max(
              1,
              Math.ceil(filtered.length / (params?.size || 10))
            ),
          };
        }}
      />

      <Box
        sx={{
          backgroundColor: COLORS.WhiteBackground,
          borderRadius: "0 0 10px 10px",
          padding: "16px",
          marginTop: "-10px",
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        <CustomLabel text="Net Profit:" />
        <CustomLabel
          text={new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
          }).format(
            rows.find((r) => r.label === "Net Profit")?.value || 0
          )}
          fontWeight="bold"
          sx={{ color: COLORS.MainColor }}
        />
      </Box>
    </Box>
  );
};

export default ProfitLossReport;
